import { getAllPageKeys } from './navPermissions';

// The set of page keys the permissions editor (PagePermissionsEditor) can control.
// Anything NOT in this list is an internal/utility route that is always reachable.
const CONTROLLED_KEYS = getAllPageKeys();

// A few real routes render a controllable page under a different path.
// Map them to the key that governs their access.
const PATH_ALIASES: Record<string, string> = {
  '/dashboard': '/',        // dashboard is reachable at both '/' and '/dashboard'
  '/events': '/calendar',   // events route renders the calendar page
};

// Priority order for "where to send a user when the page they hit is blocked".
const LANDING_CANDIDATES = [
  '/', '/telemarketing', '/contacts', '/tasks', '/mailing',
  '/whatsapp', '/campaigns', '/calendar', '/profile',
];

/** Minimal shape needed to decide access — matches the app's User object. */
export interface AccessUser {
  role?: string | null;
  allowedPages?: string[] | null;
  allowedCampaignIds?: string[] | null;
  campaignScopeAccess?: 'view' | 'edit';
  campaignContactsAccess?: boolean;
  /** module matrix of the user's permission group; null/absent = no group */
  modulePerms?: Record<string, Record<string, boolean>> | null;
}

// ── Permission-group module matrix → pages ──────────────────────────────────
// Which matrix module governs each app path (matching the rows of the group
// editor in PermissionsPage). Deliberately SEPARATE from CONTROLLED_KEYS /
// allowedPages: paths like /whatsapp or /tasks are not controllable per-user,
// but ARE governed by the group matrix. Resolution: exact match, then longest
// prefix. Paths not listed here (e.g. /profile) are never module-gated.
const MODULE_BY_PATH: Record<string, string> = {
  '/': 'reports',              // הדשבורד הראשי = דוח רוחבי על כל הארגון
  '/contacts': 'contacts',
  '/tasks': 'tasks',
  '/donations': 'donations',
  '/memberships': 'donations',
  '/income': 'donations',
  '/standing-orders': 'donations',
  '/expenses': 'expenses',
  '/campaigns': 'campaigns',
  '/tools': 'tools',
  '/gallery': 'gallery',
  '/volunteers': 'volunteers',
  '/volunteer-projects': 'volunteers',
  '/training': 'volunteers',
  '/shifts': 'shifts',
  '/calendar': 'events',
  '/events': 'events',
  '/mailing': 'mailing',
  '/whatsapp': 'whatsapp',
  '/telemarketing': 'callCenter',
  '/call-center': 'callCenter',
  '/knowledge': 'knowledge',
  '/reports': 'reports',
  '/grants': 'grants',
  '/projects': 'projects',
  '/workplan': 'projects',
  '/finances': 'finances',
  '/masav': 'finances',
  '/budget': 'budget',
  '/permissions': 'users',
  '/settings': 'settings',
};

const MODULE_PATH_KEYS = Object.keys(MODULE_BY_PATH)
  .filter(k => k !== '/')
  .sort((a, b) => b.length - a.length);

/** The matrix module governing `path`, or null when the path is not module-gated. */
export function moduleForPath(path: string): string | null {
  if (path === '/' || path === '/dashboard') return MODULE_BY_PATH['/'];
  for (const key of MODULE_PATH_KEYS) {
    if (path === key || path.startsWith(key + '/')) return MODULE_BY_PATH[key];
  }
  return null;
}

/**
 * Does the group matrix allow viewing `path`? Users without a group
 * (modulePerms null/absent) are never restricted — identical to today.
 * A governed module missing from the matrix reads as view:false, exactly
 * what the group editor displays for it.
 */
function modulePermsAllow(path: string, user: AccessUser): boolean {
  const perms = user.modulePerms;
  if (!perms || typeof perms !== 'object') return true;
  const module = moduleForPath(path);
  if (!module) return true;
  return perms[module]?.view === true;
}

function campaignScopedCanAccess(path: string, user: AccessUser): boolean {
  if (path === '/profile' || path === '/campaigns') return true;
  if (user.campaignContactsAccess && path === '/contacts') return true;
  const match = path.match(/^\/campaigns\/([^/]+)(?:\/(.*))?$/);
  if (!match || !user.allowedCampaignIds?.includes(match[1])) return false;
  const sub = match[2] || '';
  if (sub === 'dashboard' || sub === '') return true;
  return user.campaignScopeAccess === 'edit' && sub === 'builder';
}

// ── Telephonist (telemarketing_agent) profile ───────────────────────────────
// A telephonist may ONLY reach: her own call-center screen, campaigns (view),
// landing pages / forms (view) and the calendar (already server-scoped to her).
// Everything else — dashboard, finances, contacts, mailing, etc. — is blocked.
const TELEPHONIST_ALLOWED_PREFIXES = [
  '/telemarketing', '/calendar', '/campaigns', '/tools', '/profile',
];
// Within campaigns/landing-pages she has VIEW access only — the create/edit
// (builder) routes are blocked so she cannot mutate anything.
const TELEPHONIST_BLOCKED_PATTERNS: RegExp[] = [
  /^\/campaigns\/new-builder$/,
  /^\/campaigns\/[^/]+\/builder$/,
  /^\/tools\/new$/,
  /^\/tools\/[^/]+\/edit(-signature)?$/,
];

function telephonistCanAccess(path: string): boolean {
  // Block edit/builder routes first (view-only), then allow the view prefixes.
  if (TELEPHONIST_BLOCKED_PATTERNS.some(re => re.test(path))) return false;
  return TELEPHONIST_ALLOWED_PREFIXES.some(p => path === p || path.startsWith(p + '/'));
}

export function isTelephonist(user?: AccessUser | null): boolean {
  return user?.role === 'telemarketing_agent';
}

function matchesDynamic(key: string, path: string): boolean {
  const k = key.split('/');
  const p = path.split('/');
  if (k.length !== p.length) return false;
  return k.every((seg, i) => seg.startsWith(':') || seg === p[i]);
}

/**
 * Return the controllable page key that governs `pathname`, or null when the
 * route is an internal/utility route that access control does not gate.
 */
export function controllingPageKey(pathname: string): string | null {
  let path = pathname;
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  if (PATH_ALIASES[path]) path = PATH_ALIASES[path];

  // 1. Exact key (e.g. '/income', '/').
  if (CONTROLLED_KEYS.includes(path)) return path;

  // 2. Dynamic key (e.g. '/campaigns/:id/dashboard').
  for (const key of CONTROLLED_KEYS) {
    if (key.includes(':') && matchesDynamic(key, path)) return key;
  }

  // 3. Longest static prefix (sub-routes inherit the parent's access).
  //    Root '/' is never treated as a prefix — it would swallow everything.
  const prefixKeys = CONTROLLED_KEYS
    .filter(k => k !== '/' && !k.includes(':'))
    .sort((a, b) => b.length - a.length);
  for (const key of prefixKeys) {
    if (path === key || path.startsWith(key + '/')) return key;
  }

  return null;
}

/**
 * Whether `user` may open `pathname`.
 * - super_admin                     → everything.
 * - telemarketing_agent             → fixed telephonist profile (view-only).
 * - modulePerms (permission group)  → path's module must have view=true.
 * - allowedPages null / empty array → full access.
 * - allowedPages restricted list    → only listed pages; uncontrollable routes
 *                                     are always allowed.
 * modulePerms and allowedPages are independent restrictions — both must pass.
 */
export function canAccessPath(pathname: string, user?: AccessUser | null): boolean {
  const role = user?.role;
  if (role === 'super_admin') return true;

  let path = pathname;
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  if (PATH_ALIASES[path]) path = PATH_ALIASES[path];

  // קבוצות אימון הן כלי עבודה משותף לכל משתמשי הארגון: כל משתמש מערכת
  // רשאי לראות את כל המאמנים/הקבוצות ולסמן נוכחות. הדגל הארגוני עדיין
  // נאכף בנפרד ב-UI ובשרת (trainingGroups).
  if (path === '/training' || path.startsWith('/training/')) return true;

  if (Array.isArray(user?.allowedCampaignIds) && user.allowedCampaignIds.length > 0) return campaignScopedCanAccess(path, user!);

  if (role === 'telemarketing_agent') return telephonistCanAccess(path);

  if (user && !modulePermsAllow(path, user)) return false;

  const allowedPages = user?.allowedPages;
  if (!allowedPages || allowedPages.length === 0) return true;
  const key = controllingPageKey(path);
  if (!key) return true;
  return allowedPages.includes(key);
}

/** First accessible landing path for a (possibly restricted) user. */
export function firstAllowedPath(user?: AccessUser | null): string {
  if (Array.isArray(user?.allowedCampaignIds) && user.allowedCampaignIds.length > 0) return '/campaigns';
  if (user?.role === 'telemarketing_agent') return '/telemarketing';
  for (const p of LANDING_CANDIDATES) {
    if (canAccessPath(p, user)) return p;
  }
  // Narrow permission group (e.g. expenses-only): none of the usual landing
  // pages is allowed — fall back to the first page its matrix does permit.
  for (const p of Object.keys(MODULE_BY_PATH)) {
    if (p !== '/' && canAccessPath(p, user)) return p;
  }
  return '/profile'; // uncontrollable route — always reachable
}
